import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useColumnDistinct } from './queries';
import { F_BLANKS, F_NONE } from './types';
import { fmtFilterOption } from '../../utils/datetime';
import { compareValues } from '../../components/SortableHeader';

// UI-2: the module distinct list comes back as either an ISO string OR SQL
// Server's default datetime text ("Apr 1 2026 1:05PM"); fmtFilterOption renders
// BOTH to the app-wide MM/DD/YYYY HH:mm so the filter chip matches the grid cell
// EXACTLY.  The underlying value stays RAW so server-side filtering still
// matches; only the visible label is localised.
const fmtOpt = (v: string) => fmtFilterOption(v);

interface Props {
  moduleKey: string;
  column: string;
  label?: string;
  rangeDays: number | null;
  selected: string[];
  onChange: (values: string[]) => void;
  // All currently-applied column filters — this dropdown's value list cascades
  // (narrows) to match the OTHER columns' filters (the hook drops this same
  // column so its own selection never limits itself).
  cascade?: Record<string, string[]>;
  /** Order for the value list so it matches the grid (CLAUDE.md §18.6): the
   *  active sort column passes its own dir; other columns pass the grid default
   *  (DESC for an ID-first grid, else ASC). */
  sortDir?: 'asc' | 'desc';
}

/**
 * Data Center column filter — visually identical to the shared
 * `ColumnFilterDropdown` used by every other list page (same `col-flt-chevron`
 * trigger + `col-fdd` popup chrome), but server-backed: the distinct value list
 * is fetched from `/api/v1/modules/{key}/distinct/{column}` so it covers the
 * whole (paginated) dataset, not just the current page.  Selection uses the
 * F_BLANKS / F_NONBLANK sentinels the API understands.
 */
export function ColumnFilter({ moduleKey, column, label, rangeDays, selected, onChange, cascade, sortDir }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; minW: number }>({ top: 0, left: 0, minW: 240 });
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const openPanel = (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const w = 240;
      setPos({
        top: r.bottom + window.scrollY + 2,
        left: Math.min(r.left + window.scrollX, window.innerWidth - w - 8),
        minW: Math.max(240, r.width),
      });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    const t = setTimeout(() => setQ(input), 300);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)
          && btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Fetch the distinct values in the SAME direction the grid shows them
  // (§18.6). The server caps the list at TOP 500 in this direction, so passing
  // the column's effective sort dir is essential: for a DESC column (e.g. an ID
  // grid showing newest-first) `desc` returns the 500 LARGEST/newest values —
  // exactly what the grid shows — instead of the 500 smallest/oldest (the bug
  // that made the filter list start at an OLD value the grid never shows, and
  // silently drop every newer value beyond the 500-cap).
  const { data: values = [], isFetching } = useColumnDistinct(
    moduleKey, column, q, rangeDays, open, cascade, sortDir === 'desc',
  );

  // Order the DISPLAYED value list to match the grid (§18.6): the server already
  // returns the top-500 in the grid direction (above); we still sort client-side
  // with the shared grid comparator (number → date → locale) so the *within-page*
  // order is exact.  Set-membership logic below uses the raw `values`
  // (order-independent), so only the rendered list is reordered.
  const orderedValues = useMemo(() => {
    const mult = sortDir === 'desc' ? -1 : 1;
    return [...values].sort((a, b) => mult * compareValues(a, b));
  }, [values, sortDir]);

  const set = new Set(selected);
  const active = selected.length > 0;

  // The implicit "everything is selected" universe for this column: every
  // distinct value the server reported + the (Blanks) bucket. When no filter is
  // active we render every checkbox as ticked (Excel / DevExpress style) so the
  // user sees "all selected" and simply un-ticks the few they want to hide —
  // matching the shared ColumnFilterDropdown used on every other list page.
  const universe = [...values, F_BLANKS];
  const coversAll = (s: Set<string>) =>
    universe.length > 1 && s.size === universe.length && universe.every((x) => s.has(x));

  // Emit rules:
  //   • re-selecting literally everything collapses to [] (no filter — show all)
  //   • selecting nothing emits [F_NONE] — a sentinel the data never contains,
  //     so the server's `col IN ('__NONE__')` matches zero rows. This is what
  //     makes Excel-style "uncheck all" possible; an empty [] would otherwise
  //     mean "no filter = show everything" (the old bug).
  const emit = (s: Set<string>) => {
    s.delete(F_NONE);
    onChange(coversAll(s) ? [] : s.size === 0 ? [F_NONE] : [...s]);
  };

  // Real (non-sentinel) selection drives the none / partial / all states.
  const realSet = new Set(set); realSet.delete(F_NONE);
  const isNone = active && realSet.size === 0; // explicit "uncheck all"

  // First un-tick from the default state materialises the full universe, so
  // removing one value yields "all except this one". The F_NONE sentinel is
  // always stripped so ticking from the "none" state adds just that one value.
  const baseSet = () => {
    if (!active) return new Set<string>(universe);
    const s = new Set(set); s.delete(F_NONE); return s;
  };
  const toggle = (v: string) => {
    const n = baseSet();
    if (n.has(v)) n.delete(v); else n.add(v);
    emit(n);
  };

  // (Non blanks) toggles the whole non-blank value group at once.
  const nonBlankAllOn = values.length > 0 && values.every((v) => realSet.has(v));
  const toggleNonBlanks = () => {
    const n = baseSet();
    const turnOff = !active || nonBlankAllOn; // currently shown as checked
    values.forEach((v) => (turnOff ? n.delete(v) : n.add(v)));
    emit(n);
  };

  // "(All)": checked = show everything; clicking it when checked now actually
  // unchecks ALL (→ F_NONE = show none). Indeterminate only for partial states.
  const allChecked = !active || coversAll(set);
  const allIndeterminate = active && !coversAll(set) && !isNone;
  const toggleAll = () => onChange(allChecked ? [F_NONE] : []);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`col-flt-chevron${active ? ' active' : ''}${open ? ' open' : ''}`}
        title={active ? `${selected.length} filter(s) — click to edit` : `Filter ${label ?? column}`}
        onClick={openPanel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <i className={active ? 'ti ti-filter-filled' : 'ti ti-chevron-down'} />
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          className="col-fdd"
          style={{ top: pos.top, left: pos.left, minWidth: pos.minW }}
          role="dialog"
          aria-label={`Filter ${label ?? column}`}
        >
          <div className="col-fdd-hdr">
            <span>Filter {label ? `· ${label}` : ''}</span>
            <button className="col-fdd-close" onClick={() => setOpen(false)} title="Close">×</button>
          </div>

          <div className="col-fdd-search">
            <i className="ti ti-search" />
            <input
              autoFocus value={input} placeholder="Enter text to filter…"
              onChange={(e) => setInput(e.target.value)}
            />
            {input && <span className="col-fdd-clear" onClick={() => setInput('')}>×</span>}
          </div>

          <div className="col-fdd-list">
            <label className="col-fdd-row col-fdd-row-meta">
              <input type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = allIndeterminate; }}
                onChange={toggleAll} />
              <span>(Select All)</span>
            </label>
            <label className="col-fdd-row col-fdd-row-meta">
              <input type="checkbox" checked={!active || set.has(F_BLANKS)} onChange={() => toggle(F_BLANKS)} />
              <span>(Blanks)</span>
            </label>
            <label className="col-fdd-row col-fdd-row-meta">
              <input type="checkbox"
                checked={!active || nonBlankAllOn}
                ref={(el) => { if (el) el.indeterminate = active && !nonBlankAllOn && values.some((v) => set.has(v)); }}
                onChange={toggleNonBlanks} />
              <span>(Non blanks)</span>
            </label>
            <div className="col-fdd-sep" />
            {isFetching && <div className="col-fdd-empty">Loading…</div>}
            {!isFetching && values.length === 0 && <div className="col-fdd-empty">No values</div>}
            {orderedValues.map((v) => (
              <label key={v} className="col-fdd-row" title={fmtOpt(v)}>
                <input type="checkbox" checked={!active || set.has(v)} onChange={() => toggle(v)} />
                <span>{fmtOpt(v)}</span>
              </label>
            ))}
            {!isFetching && values.length >= 500 && (
              <div className="col-fdd-empty">First 500 — refine with search</div>
            )}
          </div>

          <div className="col-fdd-foot">
            <button className="col-fdd-clearall" onClick={() => onChange([])}>
              <i className="ti ti-filter-off" /> Clear filter
            </button>
            <button className="col-fdd-ok" onClick={() => setOpen(false)}>OK</button>
          </div>

          {/* Same chrome as the shared ColumnFilterDropdown so Data Center
              filters look identical to every other list page. */}
          <style>{`
            .col-fdd { position:absolute; background:#fff; border:1px solid var(--ai-line);
              border-radius:4px; box-shadow:0 6px 22px rgba(0,0,0,.18); z-index:4000;
              display:flex; flex-direction:column; max-height:360px; width:240px;
              font-family:inherit; font-size:12px; color:var(--ai-ink); }
            .col-fdd-hdr { display:flex; align-items:center; justify-content:space-between;
              padding:6px 10px; background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line);
              font-weight:700; color:var(--ai-ink); }
            .col-fdd-close { background:transparent; border:none; cursor:pointer;
              font-size:18px; line-height:1; color:var(--ai-muted); padding:0 4px; }
            .col-fdd-close:hover { color:var(--ai-ink); }
            .col-fdd-search { display:flex; align-items:center; gap:5px; padding:6px 8px;
              border-bottom:1px solid var(--ai-line-soft); background:var(--ai-header-bg); }
            .col-fdd-search i { font-size:13px; color:var(--ai-muted); }
            .col-fdd-search input { flex:1; border:1px solid var(--ai-line); border-radius:3px;
              padding:3px 6px; font-size:12px; outline:none; min-width:0; background:#fff; }
            .col-fdd-search input:focus { border-color:var(--brand-accent, var(--ai-orange)); }
            .col-fdd-clear { color:var(--ai-muted); cursor:pointer; padding:0 4px; font-size:14px; line-height:1; }
            .col-fdd-clear:hover { color:var(--ai-ink); }
            .col-fdd-list { flex:1; overflow-y:auto; padding:2px 0; }
            .col-fdd-list::-webkit-scrollbar { width:9px; }
            .col-fdd-list::-webkit-scrollbar-thumb { background:#c5cad4; border-radius:4px; }
            .col-fdd-list::-webkit-scrollbar-thumb:hover { background:#9aa0ae; }
            .col-fdd-list::-webkit-scrollbar-track { background:#f0f0f0; }
            .col-fdd-row { display:flex; align-items:center; gap:6px; padding:3px 10px;
              cursor:pointer; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
            .col-fdd-row:hover { background:#eef4fd; }
            .col-fdd-row input[type=checkbox] { width:13px; height:13px; flex-shrink:0;
              cursor:pointer; accent-color: var(--brand-accent, var(--ai-orange)); }
            .col-fdd-row span { flex:1; overflow:hidden; text-overflow:ellipsis; }
            .col-fdd-row-meta { font-weight:600; color:var(--brand-accent, var(--ai-orange)); }
            .col-fdd-row-meta:hover { background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); }
            .col-fdd-sep { height:1px; background:var(--ai-line-soft); margin:4px 8px; }
            .col-fdd-empty { padding:10px; text-align:center; color:var(--ai-muted); font-style:italic; }
            .col-fdd-foot { display:flex; gap:6px; padding:6px 8px; border-top:1px solid var(--ai-line-soft);
              background:var(--ai-header-bg); }
            .col-fdd-clearall { flex:1; display:inline-flex; align-items:center; justify-content:center;
              gap:4px; background:#fff; border:1px solid var(--ai-line); border-radius:3px; padding:4px 8px;
              font-size:11px; color:var(--ai-red); cursor:pointer; }
            .col-fdd-clearall:hover { background:#fef2f2; border-color:#fca5a5; }
            .col-fdd-ok { background:var(--brand-accent, var(--ai-orange)); color:#fff; border:none; border-radius:3px;
              padding:4px 14px; font-size:12px; font-weight:600; cursor:pointer; }
            .col-fdd-ok:hover { background:var(--brand-accent-hover, var(--ai-orange-hover)); }
          `}</style>
        </div>,
        document.body,
      )}
    </>
  );
}
